// Populates a Shopify Forms field from a URL query parameter and hides it, so the
// store handle rides along with the booking-request submission without being a
// user-visible field. The store-detail booking CTA links non-enrolled stores to
// /pages/richiesta-appuntamento?store=<handle>; Flow later resolves the store's
// email from that handle server-side. See VIS-310.
//
// The Shopify Forms inline embed (<shopify-forms-embed>) renders the form inside an
// OPEN shadow root, as a React-controlled form that mounts asynchronously. So we:
//   1. deep-query across open shadow roots (querySelector doesn't pierce them),
//   2. poll until the field mounts (a MutationObserver on the light DOM can't see
//      inside the shadow root either),
//   3. set the value through the native setter + input/change events so React's
//      controlled state captures it (a bare `.value =` is ignored), and
//   4. hide the field's row with an inline style (page CSS can't reach the shadow DOM).

import { favoriteStoreUrl } from '@theme/store-favorites';

const MOUNT_SELECTOR = '[data-appointment-prefill]';
const DEFAULT_SELECTOR =
  '[data-testid*="store_handle" i],[id*="store_handle" i],[name*="store_handle" i]';
const POLL_INTERVAL = 250;
const POLL_TIMEOUT = 15000;

/**
 * querySelector that descends into open shadow roots.
 * @param {string} selector
 * @param {Document|ShadowRoot} [root]
 * @returns {Element|null}
 */
function deepQuery(selector, root = document) {
  const direct = root.querySelector(selector);
  if (direct) return direct;
  for (const el of root.querySelectorAll('*')) {
    if (el.shadowRoot) {
      const found = deepQuery(selector, el.shadowRoot);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Sets a value on a React-controlled input via the native setter, then fires the
 * events React listens for so its internal state picks the value up.
 * @param {HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement} field
 * @param {string} value
 */
function setControlledValue(field, value) {
  const setter = Object.getOwnPropertyDescriptor(
    Object.getPrototypeOf(field),
    'value'
  )?.set;
  if (setter) setter.call(field, value);
  else field.value = value;
  field.dispatchEvent(new Event('input', { bubbles: true }));
  field.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * Hides the field's whole row (label + input) from inside the shadow tree — page
 * CSS can't reach it. The label and input are siblings inside the field container,
 * so we hide the container, not the input (whose own class also contains "field").
 */
function hideField(field) {
  const row = field.closest('[class*="FieldContainer" i]') || field.parentElement || field;
  if (row instanceof HTMLElement) row.style.display = 'none';
}

/**
 * Finds and fills the target field. Returns true once there's nothing left to do
 * (filled, or no param present) so polling can stop.
 * @param {HTMLElement} mount
 * @returns {boolean}
 */
function tryApply(mount) {
  const paramName = mount.dataset.urlParam || 'store';
  const selector = mount.dataset.fieldSelector || DEFAULT_SELECTOR;
  const shouldHide = mount.dataset.hide !== 'false';

  const value = new URLSearchParams(window.location.search).get(paramName);
  if (!value) return true; // nothing to inject — stop polling

  let field = deepQuery(selector);
  if (!field && selector !== DEFAULT_SELECTOR) field = deepQuery(DEFAULT_SELECTOR);
  if (
    !(
      field instanceof HTMLInputElement ||
      field instanceof HTMLTextAreaElement ||
      field instanceof HTMLSelectElement
    )
  ) {
    return false; // not mounted yet — keep polling
  }

  setControlledValue(field, value);
  if (shouldHide) hideField(field);
  return true;
}

/**
 * Runs `check` now and then every POLL_INTERVAL until it returns true or
 * POLL_TIMEOUT passes (the Forms embed mounts asynchronously).
 * @param {() => boolean} check
 */
function poll(check) {
  if (check()) return;
  const start = Date.now();
  const timer = setInterval(() => {
    if (check() || Date.now() - start > POLL_TIMEOUT) clearInterval(timer);
  }, POLL_INTERVAL);
}

/**
 * VIS-1097: tells the visitor which store they are booking at, under the form's
 * heading. The line is rendered by sections/appointment-form-store.liquid on
 * the store's own page (Section Rendering API), so copy and escaping stay in
 * Liquid; an unknown handle renders an empty section and shows nothing.
 * @param {HTMLElement} mount
 */
async function showStore(mount) {
  const handle = new URLSearchParams(window.location.search).get(mount.dataset.urlParam || 'store');
  if (!handle) return;

  // favoriteStoreUrl() derives the store page path from a bare handle.
  const url = `${favoriteStoreUrl({ handle })}?section_id=appointment-form-store`;
  const response = await fetch(url).catch(() => null);
  if (!response?.ok) return;
  const wrapper = new DOMParser().parseFromString(await response.text(), 'text/html').body.firstElementChild;
  const html = wrapper?.innerHTML.trim();
  if (!html) return;

  poll(() => {
    const header = deepQuery('[class*="formHeader" i]');
    if (!header) return false;
    if (header.querySelector('[data-appointment-store]')) return true;

    const line = document.createElement('p');
    line.dataset.appointmentStore = '';
    line.innerHTML = html;
    // Reuse the embed's body text class: page CSS can't reach the shadow DOM.
    const bodyText = header.getRootNode().querySelector('[class*="textBody" i]');
    const bodyClass = [...(bodyText?.classList ?? [])].find((name) => /textBody/i.test(name));
    if (bodyClass) line.className = bodyClass;
    header.append(line);
    return true;
  });
}

function init() {
  document.querySelectorAll(MOUNT_SELECTOR).forEach((mount) => {
    if (!(mount instanceof HTMLElement)) return;
    poll(() => tryApply(mount));
    showStore(mount);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
